import { Link } from "react-router";
import { ArrowRight, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormsList } from "./use-forms-list";

// Port of /app/portal/dashboard/../forms/page.tsx. Static grid of published
// forms available to the partner; no tabs, no search, no filters (the
// legacy has none). The HIDDEN_SLUGS blacklist is applied server-side in
// listFormsForPartner so deal-registration etc. don't double-surface.

export function PortalFormsListPage() {
  const { data: forms, isLoading, isError } = useFormsList();
  const visibleForms = forms ?? [];

  return (
    <div>
      <header className="mb-7">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Forms
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          Available forms
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Submit a form to the Bookboost team. Forms route to the right inbox automatically.
        </p>
      </header>

      {isLoading ? (
        <ul className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <Skeleton className="h-32 rounded-xl" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <section className="rounded-xl border bg-card p-10 text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
            Couldn’t load forms
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Please refresh the page. If the problem persists, contact support.
          </p>
        </section>
      ) : visibleForms.length === 0 ? (
        <section className="rounded-xl border bg-card p-10 text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
            No forms published yet
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The partnerships team hasn&apos;t published any general forms yet. Check back later, or
            email us in the meantime.
          </p>
          <Link
            to="/portal/support"
            className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            Visit support
            <ArrowRight className="h-3 w-3" />
          </Link>
        </section>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {visibleForms.map((form) => (
            <li key={form.id}>
              <Link
                to={`/portal/forms/${form.slug}`}
                className="group flex h-full flex-col rounded-xl border bg-card p-5 transition hover:border-accent"
              >
                <div className="flex items-center justify-between">
                  <FileText className="h-5 w-5 text-accent" />
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent" />
                </div>
                <h2 className="mt-3 font-display text-base font-semibold tracking-tight">
                  {form.name}
                </h2>
                {form.description ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {form.description}
                  </p>
                ) : null}
                <span className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                  /{form.slug}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
